import { __assign, __read } from "tslib";
import { noop } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useKey, useOnClickOutside } from '@marvelapp/hooks';
import { PENCIL_ICON } from '@marvelapp/theme';
import { Flex, Icon, Input, Position, Textarea, Tooltip } from '@marvelapp/ui';
import Highlighter from '../Highlighter';
import ButtonWrap from './ButtonWrap';
import CancelButton from './CancelButton';
import { PencilWrap } from './PencilWrap';
import RemoveButton from './RemoveButton';
import SaveButton from './SaveButton';
import Text from './Text';
export default function EditableText(_a) {
    var allowsEmptyString = _a.allowsEmptyString, _b = _a.color, color = _b === void 0 ? 'licorice' : _b, _c = _a.fontSize, fontSize = _c === void 0 ? 3 : _c, _d = _a.fontWeight, fontWeight = _d === void 0 ? 500 : _d, _e = _a.hasError, hasError = _e === void 0 ? false : _e, id = _a.id, _f = _a.inEditMode, initialInEditMode = _f === void 0 ? false : _f, _g = _a.isTextArea, isTextArea = _g === void 0 ? false : _g, _h = _a.lineHeight, lineHeight = _h === void 0 ? '29px' : _h, _j = _a.maxLength, maxLength = _j === void 0 ? undefined : _j, _k = _a.maxWidth, maxWidth = _k === void 0 ? undefined : _k, _l = _a.minHeight, minHeight = _l === void 0 ? undefined : _l, _m = _a.onCancel, onCancel = _m === void 0 ? noop : _m, onSaveValue = _a.onSaveValue, _o = _a.resize, resize = _o === void 0 ? 'both' : _o, _p = _a.searchWords, searchWords = _p === void 0 ? [] : _p, _q = _a.textAlign, textAlign = _q === void 0 ? undefined : _q, _r = _a.tooltipText, tooltipText = _r === void 0 ? 'Edit name' : _r, _s = _a.truncated, truncated = _s === void 0 ? false : _s, _t = _a.withPencil, withPencil = _t === void 0 ? true : _t, _u = _a.kind, kind = _u === void 0 ? 'light' : _u, 
    // if there is no value, the input's value comes back from backend as null, so we set as empty string to reduce console errors
    _v = _a.value, 
    // if there is no value, the input's value comes back from backend as null, so we set as empty string to reduce console errors
    initialValue = _v === void 0 ? '' : _v;
    var _w = __read(useState(initialValue), 2), value = _w[0], setValue = _w[1];
    var _x = __read(useState(hasError), 2), isValid = _x[0], setIsValid = _x[1];
    var _y = __read(useState(initialInEditMode), 2), isInEditMode = _y[0], setEditMode = _y[1];
    var inputRef = useRef();
    var editRef = useRef();
    useKey(cancelEdit, { key: 'Escape' });
    // Autofocus the input
    useEffect(function () {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInEditMode]);
    useEffect(function () {
        if (hasError) {
            setEditMode(true);
        }
    }, [hasError]);
    useEffect(function () {
        if (!isInEditMode) {
            setValue(initialValue);
        }
    }, [initialValue]);
    useOnClickOutside(editRef, function () {
        if (value.trim() === '') {
            cancelEdit();
        }
        else {
            saveValue();
        }
    });
    function saveValue(_a) {
        var _b = _a === void 0 ? {} : _a, validate = _b.validate, _c = _b.text, text = _c === void 0 ? value : _c;
        if (!allowsEmptyString && validate && text.trim() === '') {
            setIsValid(true);
            return;
        }
        setEditMode(false);
        setIsValid(false);
        if (text !== initialValue) {
            onSaveValue(text);
        }
        else {
            onCancel();
        }
    }
    function cancelEdit() {
        onCancel();
        setEditMode(false);
        setIsValid(false);
        setValue(initialValue);
    }
    function deleteText() {
        onCancel();
        setValue('');
        saveValue({ text: '' });
    }
    var inputProps = {
        height: 'auto',
        kind: isValid ? 'error' : 'default',
        letterSpacing: '-0.2px',
        ml: -10,
        'data-testid': 'editable-text-input',
        onChange: function (e) {
            setValue(e.target.value);
        },
        onFocus: function (e) { return e.target.select(); },
        onKeyDown: function (e) {
            // if the input is a text area and shift is held also don't save as the user wanted a line break
            if (isTextArea && e.shiftKey)
                return;
            if (e.key === 'Enter') {
                saveValue({ validate: true });
            }
        },
        px: 10,
        py: isTextArea ? '8px' : '2px',
        ref: inputRef,
        type: 'text',
        color: color,
        fontSize: fontSize,
        fontWeight: fontWeight,
        lineHeight: lineHeight,
        maxLength: maxLength,
        textAlign: textAlign,
        value: value,
        resize: resize,
    };
    if (isInEditMode) {
        return (React.createElement("div", { id: id, ref: editRef },
            React.createElement(Position, { display: isTextArea ? 'block' : 'inline-block', position: "relative" },
                isTextArea ? (React.createElement(Textarea, __assign({ display: "block", minHeight: minHeight, mt: -10, width: "calc(100% + 20px)" }, inputProps))) : (React.createElement(Input, __assign({}, inputProps))),
                React.createElement(ButtonWrap, { isTextArea: isTextArea },
                    allowsEmptyString && (React.createElement(RemoveButton, { "data-testid": "editable-text-delete", onClick: deleteText })),
                    React.createElement(Flex, null,
                        React.createElement(CancelButton, { "data-testid": "editable-text-cancel", onClick: cancelEdit }),
                        React.createElement(SaveButton, { "data-testid": "editable-text-confirm", onClick: function () { return saveValue({ validate: true }); } }))))));
    }
    return (React.createElement(Text, { color: color, "data-testid": "editable-text", fontSize: fontSize, fontWeight: fontWeight, id: id, kind: kind, lineHeight: lineHeight, maxWidth: maxWidth, minHeight: minHeight, mt: isTextArea ? -10 : 0, onClick: function () { return setEditMode(true); }, pt: isTextArea ? '8px' : '2px', textAlign: textAlign, truncated: truncated, width: isTextArea ? 'calc(100% + 20px)' : undefined },
        React.createElement(Highlighter, { searchWords: searchWords, textToHighlight: value }),
        withPencil && (React.createElement(PencilWrap, { isTextArea: isTextArea },
            React.createElement(Tooltip, { content: tooltipText, "data-testid": "editable-text-edit-tooltip", position: "bottom" },
                React.createElement(Icon, { color: "silver", "data-testid": "editable-text-edit-icon", display: "block", height: 20, hoverColor: "slate", paths: PENCIL_ICON, width: 20 }))))));
}
